import React, { createContext, useContext } from "react";

// Initial state for the AuthContext, including the current user type and a function to refresh user data.
const initialState = {
  currentUserType: "",
  refreshUserData: () => {},
};

// Create a Context for managing authentication-related data.
const AuthContext = createContext(initialState);

/**
 * Hook to access the authentication context data, specifically the current user type and the refreshUserData function.
 *
 * @returns {Object} - Contains the currentUserType and refreshUserData from the context.
 */
export const UserCommonData = () => useContext(AuthContext);

/**
 * CommonData component is used to provide the authentication context to its children.
 *
 * This component is responsible for:
 * - Determining the current user type (either from the provided userData or defaulting to an empty string).
 * - Providing the context with the user type and a function (`refreshUserData`) to refresh the user data.
 *
 * @param {Object} props - The component's props.
 * @param {Object} props.userData - The user data that contains information like `userType`.
 * @param {Function} props.refreshUserData - Function to refresh user data.
 * @returns {JSX.Element} - Provides the AuthContext to the child components.
 */
export const CommonData = ({ children, userData, refreshUserData }) => {
  // Determine the userType from the provided userData or default to an empty string if userData is null or undefined.
  const userType =
    userData === null || userData === undefined
      ? ""
      : typeof userData === "string"
        ? userData
        : userData?.userType
          ? userData.userType
          : -1;

  return (
    <AuthContext.Provider
      value={{ currentUserType: userType, refreshUserData }}
    >
      {children}
    </AuthContext.Provider>
  );
};
